import { useEffect, useState, useRef } from 'react'
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useAuth } from '../../components/auth/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { getProfile, updateProfile, changePassword, uploadAvatar } from '../../actions/authActions'
import AxiosControl from '../../components/axios/AxiosControl'
import { toastSuccess, toastError } from '../../components/swal/toast'

const Axios = AxiosControl()

export default function Profile() {
    const { t } = useTranslation()
    const { clientauth, setClientauth } = useAuth()
    const dispatch = useDispatch()
    const { profile, loading, profileUpdating, profileUpdateError, profileUpdateSuccess, avatar, avatarUploading } = useSelector(state => state.auth)

    const [form, setForm] = useState({ name: '', email: '', phone: '' })
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
    const [showPasswordForm, setShowPasswordForm] = useState(false)
    const [preview, setPreview] = useState(null)
    const fileInputRef = useRef(null)

    useEffect(() => {
        dispatch(getProfile())
    }, [dispatch])

    useEffect(() => {
        if (profile) {
            setForm({
                name: profile.user?.name || '',
                email: profile.user?.email || '',
                phone: profile.teacher?.phone || profile.odooContact?.phone || ''
            })
            if (!avatar && profile.user?.avatar) {
                dispatch({ type: 'AVATAR_SUCCESS', payload: profile.user.avatar })
            }
        }
    }, [profile])

    useEffect(() => {
        if (profileUpdateSuccess) {
            toastSuccess(profileUpdateSuccess)
            if (clientauth) {
                setClientauth({ ...clientauth, name: form.name, email: form.email })
            }
        }
    }, [profileUpdateSuccess])

    useEffect(() => {
        if (profileUpdateError) {
            toastError(profileUpdateError)
        }
    }, [profileUpdateError])

    const handleAvatarClick = () => {
        fileInputRef.current?.click()
    }

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setPreview(URL.createObjectURL(file))
        const formData = new FormData()
        formData.append('image', file)
        try {
            const { data, status } = await Axios.post('/api/upload/uploadimage', formData, {
                headers: { auth: localStorage.getItem('jwt'), 'Content-Type': 'multipart/form-data' }
            })
            if (status === 200) {
                dispatch(uploadAvatar(data))
            }
        } catch (error) {
            toastError(t('teacher.profile.uploadImageError'))
        }
    }

    const handleSaveProfile = (e) => {
        e.preventDefault()
        dispatch(updateProfile(form))
    }

    const handleChangePassword = (e) => {
        e.preventDefault()
        if (passwordForm.newPassword.length < 6) {
            toastError(t('teacher.profile.passwordMinLength'))
            return
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toastError(t('teacher.profile.passwordsNoMatch'))
            return
        }
        dispatch(changePassword(passwordForm.currentPassword, passwordForm.newPassword))
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
        setShowPasswordForm(false)
    }

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <h3 className='fw-bold mb-1'>{t('teacher.profile.title')}</h3>
                    <p className='text-muted mb-0'>{t('teacher.profile.subtitle')}</p>
                </Col>
            </Row>

            <Row className='mt-4'>
                <Col xs={12} md={4}>
                    <Card className='border-0 shadow-sm rounded-4'>
                        <Card.Body className='text-center p-4'>
                            <div className='position-relative d-inline-block mb-3'>
                                <div
                                    onClick={handleAvatarClick}
                                    className='rounded-circle d-inline-flex align-items-center justify-content-center'
                                    style={{
                                        width: '80px', height: '80px',
                                        backgroundColor: '#eff6ff',
                                        cursor: 'pointer',
                                        overflow: 'hidden'
                                    }}>
                                    {avatarUploading ? (
                                        <span className='spinner-border text-primary'></span>
                                    ) : preview || avatar ? (
                                        <img src={preview || avatar} alt='avatar' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <i className='fa-solid fa-user-tie' style={{ fontSize: '32px', color: '#3b82f6' }}></i>
                                    )}
                                </div>
                                <span className='position-absolute bottom-0 end-0 badge bg-primary rounded-circle p-1'
                                    style={{ width: '24px', height: '24px', cursor: 'pointer' }}>
                                    <i className='fa-solid fa-camera' style={{ fontSize: '10px' }}></i>
                                </span>
                            </div>
                            <input ref={fileInputRef} type='file' accept='image/*' className='d-none' onChange={handleAvatarChange} />
                            <h5 className='fw-bold mb-0'>{form.name || t('teacher.profile.role')}</h5>
                            <p className='text-muted mb-2' style={{ fontSize: '13px' }}>{t('teacher.profile.role')}</p>
                            <p className='text-muted mb-0' style={{ fontSize: '13px' }}>{form.email}</p>
                            {profile?.teacher?.specializations?.length > 0 && (
                                <div className='mt-3'>
                                    {profile.teacher.specializations.map((s, i) => (
                                        <span key={i} className='badge bg-light text-dark border me-1 mb-1'>{s}</span>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} md={8}>
                    <Card className='border-0 shadow-sm rounded-4 mb-4'>
                        <Card.Body className='p-4'>
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <h5 className='fw-bold mb-0'>{t('teacher.profile.information')}</h5>
                            </div>
                            <Form onSubmit={handleSaveProfile}>
                                <Row className='g-3'>
                                    <Col md={6}>
                                        <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>{t('teacher.profile.fullname')}</Form.Label>
                                        <Form.Control type='text' style={{ borderRadius: '10px' }}
                                            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>{t('teacher.profile.email')}</Form.Label>
                                        <Form.Control type='email' style={{ borderRadius: '10px' }}
                                            value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>{t('teacher.profile.phone')}</Form.Label>
                                        <Form.Control type='text' style={{ borderRadius: '10px' }}
                                            value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                                    </Col>
                                </Row>
                                <div className='mt-4'>
                                    <Button type='submit' className='btn-primary rounded-3 px-4' disabled={profileUpdating}>
                                        {profileUpdating ? (
                                            <span className='spinner-border spinner-border-sm align-middle'></span>
                                        ) : (
                                            <><i className='fa-solid fa-check me-1'></i> {t('teacher.profile.save')}</>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    <Card className='border-0 shadow-sm rounded-4'>
                        <Card.Body className='p-4'>
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <h5 className='fw-bold mb-0'>
                                    <i className='fa-solid fa-lock me-2'></i>{t('teacher.profile.password')}
                                </h5>
                                <Button variant='outline-secondary' size='sm' className='rounded-3'
                                    onClick={() => setShowPasswordForm(!showPasswordForm)}>
                                    {showPasswordForm ? t('global.cancel') : t('teacher.profile.changePassword')}
                                </Button>
                            </div>

                            {showPasswordForm && (
                                <Form onSubmit={handleChangePassword}>
                                    <Row className='g-3'>
                                        <Col md={12}>
                                            <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>{t('teacher.profile.currentPassword')}</Form.Label>
                                            <Form.Control type='password' style={{ borderRadius: '10px' }}
                                                value={passwordForm.currentPassword}
                                                onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                                required />
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>{t('teacher.profile.newPassword')}</Form.Label>
                                            <Form.Control type='password' style={{ borderRadius: '10px' }}
                                                value={passwordForm.newPassword}
                                                onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                                required />
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label style={{ fontSize: '13px', color: '#6b7280' }}>{t('teacher.profile.confirmPassword')}</Form.Label>
                                            <Form.Control type='password' style={{ borderRadius: '10px' }}
                                                value={passwordForm.confirmPassword}
                                                onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                                required />
                                        </Col>
                                    </Row>
                                    <div className='mt-3'>
                                        <Button type='submit' variant='outline-primary' className='rounded-3 px-4' disabled={profileUpdating}>
                                            {profileUpdating ? (
                                                <span className='spinner-border spinner-border-sm align-middle'></span>
                                            ) : (
                                                <><i className='fa-solid fa-key me-1'></i> {t('teacher.profile.changePassword')}</>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            )}

                            {!showPasswordForm && (
                                <p className='text-muted mb-0' style={{ fontSize: '13px' }}>
                                    {t('teacher.profile.passwordInfo')}
                                </p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}
